import React, { useEffect, useState } from 'react';
import { Box, Tabs, Tab, Typography, Skeleton } from '@mui/material';
import { MdLanguage, MdOutlineAssignment } from 'react-icons/md';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { a11yProps, CustomTabPanel } from 'components/Common/CustomTabPanel';
import QAIcon from 'assets/icons/QAIcon';
import { useGetKnowledgeBase } from 'apis/business/setting/knowledgeBase/hook';
import QuestionAnswer from './Components/QuestionAnswer';
import DatasetsPartial from './Components/DatasetsPartial';
import GoBackButtonIcon from '@/assets/icons/GoBackButtonIcon';

import { IconButton, TextField } from '@mui/material';
import { MdOutlineModeEditOutline } from 'react-icons/md';
import { IoClose } from 'react-icons/io5';
import { IoMdCheckmark } from 'react-icons/io';
import { useCreateOrUpdateKnowledgeBase } from 'apis/business/setting/knowledgeBase/hook';

export default function Dataset() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state;
  const isKnowledgeBaseSettingsPage = location.pathname?.includes('/setting/knowledge-bases');

  const getTabIndexFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return Number(params.get('tab') ?? 0);
  };

  const [value, setValue] = useState(getTabIndexFromUrl);
  const { data: knowledgeBase, isLoading: loadingKb } = useGetKnowledgeBase();
  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    navigate({ search: `?tab=${newValue}` });
  };

  const { mutate: updateKnowledgeBase, isPending } = useCreateOrUpdateKnowledgeBase(true);

  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');

  useEffect(() => {
    if (knowledgeBase?.name) {
      setNameValue(knowledgeBase.name);
    }
  }, [knowledgeBase]);

  const handleSaveName = () => {
    if (!nameValue?.trim()) return;

    updateKnowledgeBase(
      {
        knowledgebase: {
          ...knowledgeBase,
          name: nameValue
        }
      },
      {
        onSuccess: () => setEditingName(false)
      }
    );
  };

  return (
    <>
      <Box display='flex' alignItems='start' mb={3} gap={0.8}>
        <Box display='flex' alignItems='center' justifyContent='center' mt={0.5}>
          {loadingKb ? (
            <Skeleton variant='circular' width={28} height={28} />
          ) : (
            <Box
              onClick={() => navigate(isKnowledgeBaseSettingsPage ? '/setting/knowledge-bases' : (from ?? -1))}
              sx={{ cursor: 'pointer' }}
            >
              <GoBackButtonIcon />
            </Box>
          )}
        </Box>

        <Box display='flex' alignItems='center' gap={1}>
          {loadingKb ? (
            <>
              <Skeleton variant='text' width={180} height={28} />
              <Skeleton variant='circular' width={28} height={28} />
            </>
          ) : !editingName ? (
            <>
              <Typography variant='h6' fontWeight={500} mt={0.5}>
                {knowledgeBase?.name}
              </Typography>

              <IconButton size='small' onClick={() => setEditingName(true)}>
                <MdOutlineModeEditOutline size={20} />
              </IconButton>
            </>
          ) : (
            <>
              <TextField size='small' value={nameValue} onChange={(e) => setNameValue(e.target.value)} autoFocus />

              <IconButton size='small' onClick={handleSaveName} disabled={isPending}>
                <IoMdCheckmark size={20} />
              </IconButton>

              <IconButton
                size='small'
                onClick={() => {
                  setNameValue(knowledgeBase?.name || '');
                  setEditingName(false);
                }}
              >
                <IoClose size={20} />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
      <Box
        bgcolor='background.paper'
        mb={3}
        py={0.5}
        px={0.5}
        borderRadius={2}
        border='1px solid rgba(240,239,239,0.9)'
      >
        {loadingKb ? (
          <Box display='flex' gap={2} px={2} py={1}>
            <Skeleton variant='rounded' width='33%' height={36} />
            <Skeleton variant='rounded' width='33%' height={36} />
            <Skeleton variant='rounded' width='33%' height={36} />
          </Box>
        ) : (
          <Tabs value={value} onChange={handleChange} variant='fullWidth'>
            <Tab
              label={
                <Box display='flex' gap={1}>
                  <QAIcon />
                  Q&A
                </Box>
              }
              {...a11yProps('dataset-tab', 'dataset-panel', 0)}
            />
            <Tab
              label={
                <Box display='flex' gap={1} alignItems='center'>
                  <MdLanguage size={22} />
                  Web
                </Box>
              }
              {...a11yProps('dataset-tab', 'dataset-panel', 1)}
            />
            <Tab
              label={
                <Box display='flex' alignItems='center' gap={1}>
                  <MdOutlineAssignment size={22} />
                  Documents
                </Box>
              }
              {...a11yProps('dataset-tab', 'dataset-panel', 2)}
            />
          </Tabs>
        )}
      </Box>

      <CustomTabPanel value={value} id='dataset-tab' ariaControl='dataset-tabpanel' index={0}>
        <QuestionAnswer isLoading={loadingKb} />
      </CustomTabPanel>

      <CustomTabPanel value={value} id='dataset-tab' ariaControl='dataset-tabpanel' index={1}>
        <DatasetsPartial type='website' title='Web URL Datasets' />
      </CustomTabPanel>

      <CustomTabPanel id='dataset-tab' ariaControl='dataset-tabpanel' value={value} index={2}>
        <DatasetsPartial type='document' title='Documents Dataset' />
      </CustomTabPanel>

      <Outlet />
    </>
  );
}
